// Ask Firefly answer panel — CTAs, companion tour, platform-owner flag.

import { useCallback, useState } from "react";
import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import type { AuthContextValue } from "@/contexts/AuthContext";
import {
  canFlagFireflyAskMismatch,
  flagFireflyAskToDevSync,
} from "@/lib/firefly/flagFireflyAskToDevSync";
import type { FireflyAskResolution } from "@/lib/firefly/resolveFireflyAsk";

export type FireflyAskHighlightCallout = {
  title: string;
  body: string;
};

export type AskFireflyResultProps = {
  queryText: string;
  resolution: FireflyAskResolution;
  currentMode: PrimaryMode;
  auth: AuthContextValue;
  scopedOrganizationId: string | null;
  getToken: () => Promise<string | null>;
  onNavigate: () => void;
  onHighlightHere: (targetId: string, callout: FireflyAskHighlightCallout) => void;
  onStartCompanionTour?: () => void;
};

export function AskFireflyResult(props: AskFireflyResultProps) {
  const {
    queryText,
    resolution,
    currentMode,
    auth,
    scopedOrganizationId,
    getToken,
    onNavigate,
    onHighlightHere,
    onStartCompanionTour,
  } = props;
  const [flagStatus, setFlagStatus] = useState<"idle" | "busy" | "done" | "error">("idle");
  const canFlag = canFlagFireflyAskMismatch(auth);

  const onFlagMismatch = useCallback(async () => {
    if (!canFlag || flagStatus === "busy") return;
    setFlagStatus("busy");
    const result = await flagFireflyAskToDevSync({
      queryText,
      resolution,
      mode: currentMode,
      auth,
      scopedOrganizationId,
      getToken,
    });
    setFlagStatus(result.ok ? "done" : "error");
  }, [auth, canFlag, currentMode, flagStatus, getToken, queryText, resolution, scopedOrganizationId]);

  if (resolution.kind === "not_found") {
    return (
      <div className="space-y-2">
        <p className="text-xs text-gray-700 dark:text-zinc-300">{resolution.message}</p>
        {canFlag ? (
          <FlagMismatchButton status={flagStatus} onClick={onFlagMismatch} />
        ) : null}
      </div>
    );
  }

  if (resolution.kind === "denied") {
    return (
      <p className="text-xs text-amber-800 dark:text-amber-200" role="status">
        {resolution.message}
      </p>
    );
  }

  const alreadyOnTargetMode = resolution.navigateTo?.mode === currentMode;
  const navigateLabel = alreadyOnTargetMode
    ? "Show me"
    : resolution.navigateTo?.ctaLabel;

  const onShowMe = () => {
    if (alreadyOnTargetMode && resolution.highlightTarget) {
      onHighlightHere(resolution.highlightTarget, {
        title: "Here's how",
        body: resolution.answer,
      });
      return;
    }
    onNavigate();
  };

  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-800 dark:text-zinc-200">{resolution.answer}</p>
      <div className="flex flex-wrap gap-2">
        {resolution.navigateTo ? (
          <button
            type="button"
            onClick={onShowMe}
            className="rounded-lg border border-emerald-500/50 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-800 hover:bg-emerald-50 dark:border-emerald-600/50 dark:bg-emerald-950/30 dark:text-emerald-100 dark:hover:bg-emerald-950/50"
          >
            {navigateLabel}
          </button>
        ) : null}
        {resolution.companionTourId && onStartCompanionTour ? (
          <button
            type="button"
            onClick={onStartCompanionTour}
            className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-800 hover:bg-gray-50 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-100 dark:hover:bg-zinc-800"
          >
            Take a tour
          </button>
        ) : null}
        {canFlag ? (
          <FlagMismatchButton status={flagStatus} onClick={onFlagMismatch} />
        ) : null}
      </div>
    </div>
  );
}

function FlagMismatchButton(props: {
  status: "idle" | "busy" | "done" | "error";
  onClick: () => void;
}) {
  const { status, onClick } = props;
  const label =
    status === "busy"
      ? "Sending…"
      : status === "done"
        ? "Queued for dev sync"
        : status === "error"
          ? "Could not queue"
          : "Flag wrong answer";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={status === "busy" || status === "done"}
      className="rounded-lg border border-amber-400/60 bg-amber-50/80 px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-100 disabled:opacity-60 dark:border-amber-600/50 dark:bg-amber-950/30 dark:text-amber-100"
    >
      {label}
    </button>
  );
}
